<template>
    <v-container class="pa-5" fluid>
        <DeleteProcessEntryDialog
            :process-entry="processEntry"
            :is-open="deleteProcessEntryDialog"
            :action-loading="actionLoading"
            @on-confirm="deleteProcessEntry"
            @on-close="closeDeleteProcessEntryDialog"
        />
        <DeleteProcessSubjectDialog
            :process-subject="processSubject"
            :is-open="deleteProcessSubjectDialog"
            :action-loading="actionLoading"
            @on-confirm="deleteProcessSubject"
            @on-close="closeDeleteProcessSubjectDialog"
        />
        <AddProcessEntryDialog
            :process-entry="processEntry"
            :parent-folder-id="currentFolderId"
            :parent-folder-name="currentFolderName"
            :action-loading="actionLoading"
            :is-open="saveProcessEntryDialog"
            @on-save="saveProcessEntry"
            @on-close="closeProcessEntryDialog"
        />
        <AddProcessSubjectDialog
            :new-process-subject="newProcessSubject"
            :is-open="saveProcessSubjectDialog"
            :parent-folder-id="currentFolderId"
            :parent-folder-name="currentFolderName"
            :action-loading="actionLoading"
            @on-save="saveProcessSubject"
            @on-close="closeProcessSubjectDialog"
        />

        <page-title>Baza wiedzy</page-title>

        <v-row>
            <v-col cols="auto">
                <v-btn
                    v-if="hasCreatePermission"
                    color="secondary" 
                    medium 
                    data-sentry-label="Dodaj folder" 
                    @click="showCreateProcessSubjectDialog"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj folder
                </v-btn>
            </v-col>
            <v-col cols="auto">
                <v-btn 
                    v-if="isAllowedToAddEntry && hasCreatePermission" 
                    color="secondary" 
                    medium 
                    data-sentry-label="Dodaj wpis" 
                    :disabled="actionLoading" 
                    @click="showCreateProcessEntryDialog"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj wpis
                </v-btn>
            </v-col>
        </v-row>
        <v-row align="center" justify="end">
            <v-col cols="auto">
                <v-text-field
                    v-model="filters.text"
                    label="Wyszukaj..."
                    append-icon="mdi-magnify"
                    :loading="searchLoading"
                    single-line
                    solo
                    clearable
                    :error-messages="searchFieldErrors"
                    clear-icon="mdi-close-circle-outline"
                    @click:append="changeFilter()"
                    @keyup.enter="changeFilter()"
                    @click:clear="onClear()"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <template>
                    <v-tabs
                        v-model="tab"
                        background-color="#fafafa"
                        show-arrows
                    >
                        <v-tab
                            v-for="tabData in tabsTitles"
                            :key="tabData.id"
                            @click="redirectDestination(tabData)"
                        >
                            {{ tabData.name }}
                        </v-tab>
                    </v-tabs>
                </template>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-data-iterator
                    :items="currentLevelItems"
                    item-key="name"
                    sort-by="name"
                    hide-default-footer
                    :items-per-page="-1"
                    no-data-text=""
                >
                    <template #default="props">
                        <v-row>
                            <v-col
                                v-for="(item, itemIndex) in props.items"
                                :key="itemIndex"
                                cols="12"
                                sm="6"
                                lg="3"
                            >
                                <v-card>
                                    <v-toolbar flat dense color="grey lighten-4">
                                        <v-container fluid class="pa-0">
                                            <v-row>
                                                <v-col cols="1">
                                                    <v-tooltip :open-delay="tooltipDelay" top>
                                                        <template #activator="{on}">
                                                            <v-btn
                                                                color="primary"
                                                                small
                                                                icon
                                                                tile
                                                                data-sentry-label="Otwórz folder"
                                                                v-on="on"
                                                                @click="fetchSubjectData(item.id)"
                                                            >
                                                                <v-icon>mdi-folder-open</v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Otwórz folder</span>
                                                    </v-tooltip>
                                                </v-col>
                                                <v-col cols="9">
                                                    <v-toolbar-title v-if="item.name" class="text-subtitle-1">
                                                        {{ formattedName(item.name) }}
                                                    </v-toolbar-title>
                                                </v-col>
                                                <v-col cols="1">
                                                    <v-tooltip :open-delay="tooltipDelay" top>
                                                        <template #activator="{on}">
                                                            <v-btn
                                                                v-if="hasDeletePermission"
                                                                color="pink"
                                                                small
                                                                icon
                                                                tile
                                                                data-sentry-label="Usuń folder"
                                                                v-on="on"
                                                                @click="showDeleteItemDialog(item)"
                                                            >
                                                                <v-icon>mdi-delete</v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Usuń folder</span>
                                                    </v-tooltip>
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-toolbar>
                                </v-card>
                            </v-col>
                        </v-row>
                    </template>
                </v-data-iterator>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-data-iterator
                    :items="processEntries"
                    item-key="name"
                    sort-by="name"
                    hide-default-footer
                    :items-per-page="-1"
                    :no-data-text="noEntriesText"
                >
                    <template #default="props">
                        <v-row>
                            <v-col
                                v-for="(item, itemIndex) in props.items"
                                :key="itemIndex"
                                cols="12"
                                sm="6"
                                lg="3"
                            >
                                <v-card>
                                    <div class="grey lighten-4">
                                        <v-container fluid class="pa-0">
                                            <v-row>
                                                <v-col cols="1" class="ml-3">
                                                    <v-tooltip :open-delay="tooltipDelay" top>
                                                        <template #activator="{on}">
                                                            <v-btn
                                                                icon
                                                                small
                                                                tile
                                                                data-sentry-label="Szczegóły wpisu"
                                                                v-on="on"
                                                                @click="readEntry(item.id)"
                                                            >
                                                                <v-icon color="success">
                                                                    mdi-file-document
                                                                </v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Szczegóły wpisu</span>
                                                    </v-tooltip>
                                                </v-col>
                                                <v-col cols="8">
                                                    <span v-if="item.name" class="text-subtitle-1">
                                                        {{ item.name }}
                                                    </span>
                                                </v-col>
                                                <v-col cols="1">
                                                    <v-tooltip :open-delay="tooltipDelay" top>
                                                        <template #activator="{on}">
                                                            <v-btn
                                                                icon
                                                                small
                                                                tile
                                                                data-sentry-label="Edytuj"
                                                                :disabled="!hasEditPermission"
                                                                v-on="on"
                                                                @click="editEntry(item.id)"
                                                            >
                                                                <v-icon color="teal">
                                                                    mdi-pencil
                                                                </v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Edytuj</span>
                                                    </v-tooltip>
                                                </v-col>
                                                <v-col cols="1">
                                                    <v-tooltip :open-delay="tooltipDelay" top>
                                                        <template #activator="{on}">
                                                            <v-btn
                                                                v-if="hasDeletePermission"
                                                                icon
                                                                small
                                                                tile
                                                                data-sentry-label="Usuń wpis"
                                                                v-on="on"
                                                                @click="showDeleteItemDialog(item)"
                                                            >
                                                                <v-icon color="pink">
                                                                    mdi-delete
                                                                </v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Usuń wpis</span>
                                                    </v-tooltip>
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>
                    </template>
                </v-data-iterator>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import AddProcessEntryDialog from './AddProcessEntryDialog'
import AddProcessSubjectDialog from './AddProcessSubjectDialog'
import DeleteProcessSubjectDialog from './DeleteProcessSubjectDialog'
import DeleteProcessEntryDialog from './DeleteProcessEntryDialog'
import tooltipDelay from '@/mixins/tooltip'
import filters from '@/mixins/dataTableFilters'
import { errorHandler } from '@/utils/errorHandler'
import router from '@/router'
import store from '@/./store'
import { hasUserPermission } from '@/utils/permissionsHandler'
import { minLength } from 'vuelidate/lib/validators'

export default {
    name: 'WikiSearch',
    components: {
        PageTitle, AddProcessEntryDialog, AddProcessSubjectDialog, DeleteProcessEntryDialog, DeleteProcessSubjectDialog
    },
    mixins: [
        filters,
        tooltipDelay
    ],
    beforeRouteEnter(to, from, next) {
        if ('returnToSubjectId' in to.params) {
            store.dispatch('wiki/getProcessSubject', {'processSubjectId': to.params.returnToSubjectId, query: ''})
                .then(response => {
                    next(vm => {
                        vm.processSubject = response.data
                        vm.currentLevelItems = vm.processSubject.children
                        vm.currentFolderId = vm.processSubject.id
                        vm.currentFolderName = vm.processSubject.name
                        vm.processEntries = vm.processSubject.entries
                        vm.nestedLevelsParentsIds[response.data.nestingLevel] = response.data.id
                        vm.tabsTitles = to.params.tabsTitles
                        vm.tab = to.params.tabsTitles.length - 1
                    })

                })
                .catch(error => {
                    errorHandler(error)
                })
        } else {
            store.dispatch('wiki/getProcessSubjects', '?primarySubjects=true')
                .then(response => {
                    next(vm => {
                        const processSubjects = response.data.results, items = []
    
                        processSubjects.map(subject => subject.children = [])
                        processSubjects.forEach(subject => {
                            items.push(subject)
                        })
    
                        vm.processSubjects = processSubjects
                        vm.items = items
                        vm.currentLevelItems = vm.items
                    })
                })
                .catch(error => {
                    next(error)
                    errorHandler(error)
                })
        }
    },
    data() {
        return {
            actionLoading: false,
            processEntry: {},
            processSubject: {},
            newProcessSubject: {},
            saveProcessSubjectDialog: false,
            saveProcessEntryDialog: false,
            items: [],
            currentLevelItems: [],
            children: [],
            processEntries: [],
            processSubjects: [],
            deleteProcessEntryDialog: false,
            deleteProcessSubjectDialog: false,
            currentFolderId: null,
            currentFolderName: null,
            searchLoading: false,
            nestedLevelsParentsIds: {},
            tab: null,
            tabsTitles: [{'id': -1, 'subjectId': null, 'name': 'STRONA GŁÓWNA'}],
        }
    },
    validations() {
        const rules = {
            filters: {
                text: {
                    minLength: minLength(3)
                },
            }
        }

        return rules
    },
    computed: {
        noEntriesText() {
            return this.currentLevelItems.length > 0 ? '' : 'brak danych'
        },
        isAllowedToAddEntry() {
            return this.currentFolderId
        },
        hasDeletePermission() {
            return hasUserPermission('wiki_delete')
        },
        hasEditPermission() {
            return hasUserPermission('wiki_update')
        },
        hasCreatePermission() {
            return hasUserPermission('wiki_create')
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] !== null && typeof filters[item] !== 'undefined') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                } 
            }
            query = '?' + query.substring(1)

            return query
        },
        searchFieldErrors() {
            const errors = []

            if (this.$v.filters.text.$dirty)
                errors.push('Proszę wpisać co najmniej 3 znaki.')

            return errors
        },
    },
    methods: {
        formattedName(name) {
            return name.length <= 45 ? name : `${name.slice(0, 42)}...`
        },
        setInitialNestedLevelsParentsIds() {
            this.nestedLevelsParentsIds = {}
        },
        setInitialTabTitles() {
            this.tabTitles = [{'id': -1, 'subjectId': null, 'name': 'STRONA GŁÓWNA'}]
        },
        resetTabs(tab) {
            this.tabsTitles = this.tabsTitles.filter(element => element.id <= tab.id)
            this.tab = tab.id + 1
        },
        redirectDestination(tab) {
            this.resetTabs(tab)
            tab.id < 0 ? this.getProcessSubjects() : this.fetchSubjectData(tab.subjectId)
        },
        getProcessSubjects() {
            this.searchLoading = true
            this.setInitialTabTitles()
            this.filters['primarySubjects'] = true
            this.$store.dispatch('wiki/getProcessSubjects', this.filterQuery)
                .then(response => {
                    const processSubjects = response.data.results, items = []

                    processSubjects.map(subject => subject.children = [])
                    processSubjects.forEach(subject => {
                        items.push(subject)
                    })

                    this.processSubjects = processSubjects
                    this.processSubject = {}
                    this.processEntries = []
                    this.items = items
                    this.currentLevelItems = items
                    this.currentFolderId = null
                    this.currentFolderName = null
                    this.setInitialNestedLevelsParentsIds()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(
                    this.searchLoading = false
                )
        },
        fetchSubjectData(subjectId) {
            const query = this.filters['text'] ? `?text=${this.filters['text']}` : ''
            
            this.$store.dispatch('wiki/getProcessSubject', {'processSubjectId': subjectId, 'query': query})
                .then(response => {
                    this.processSubject = response.data
                    this.currentLevelItems = this.processSubject.children
                    this.currentFolderId = this.processSubject.id
                    this.currentFolderName = this.processSubject.name
                    this.processEntries = this.processSubject.entries
                    this.nestedLevelsParentsIds[response.data.nestingLevel] = response.data.id
                    this.setTabsData(response.data)

                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        setTabsData(data) {
            if (!this.tabsTitles.some((element) => element.id === data.nestingLevel)) {
                const formattedName = data.name.length <= 32 ? data.name : `${data.name.slice(0, 29)}...`
                
                this.tabsTitles.push({'id': data.nestingLevel, 'subjectId': data.id, 'name': formattedName})
            }

            this.tabsTitles = this.tabsTitles.filter(element => element.id <= data.nestingLevel)
            this.tab = data.nestingLevel + 1
        },
        showCreateProcessSubjectDialog() {
            this.saveProcessSubjectDialog = true
        },
        showCreateProcessEntryDialog() {
            this.saveProcessEntryDialog = true
        },
        editEntry(entryId) {
            router.push({ name: 'wikiWikiEntry', params: { 
                id: entryId, 
                actionName: 'updateProcessEntry',
                ancestorsNames: this.tabsTitles
            }})
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeProcessSubjectDialog() {
            this.saveProcessSubjectDialog = false
        },
        closeProcessEntryDialog() {
            this.actionLoading = false
            this.saveProcessEntryDialog = false

            setTimeout(() => {
                this.processEntry = {}
            }, 500)
        },
        saveProcessEntry(attachments) {
            this.$store.dispatch('toggleCircularLoader', true)
            this.actionLoading = true
            this.$store.dispatch('wiki/saveProcessEntry', this.processEntry)
                .then((response) => {
                    this.processEntries.push(response.data)

                    if (attachments.length > 0) {
                        const formData = new FormData()

                        attachments.forEach(item => {
                            formData.append('file', item)
                        })
                        formData.append('processEntryId', response.data.id)
                        this.$store.dispatch('wiki/createAttachment', formData)
                            .then(() => {
                                this.saveProcessEntryDialog = false
                                this.attachments = []
                            })
                            .catch(error => {
                                errorHandler(error)
                            })
                    }
                    this.$store.dispatch('showNotification', {
                        type: 'success',
                        message: 'Dodano wpis.'
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeProcessEntryDialog()
                    this.$store.dispatch('toggleCircularLoader', false)
                })

        },
        saveProcessSubject() {
            this.$store.dispatch('toggleCircularLoader', true)
            this.actionLoading = true
            this.$store.dispatch('wiki/saveProcessSubject', this.newProcessSubject)
                .then((response) => {
                    this.closeProcessSubjectDialog()
                    this.currentLevelItems.push(response.data)
                    this.saveProcessSubjectDialog = false
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.newProcessSubject = {}
                    this.actionLoading = false
                    this.$store.dispatch('toggleCircularLoader', false)
                })
        },
        readEntry(entryId) {
            router.push({ name: 'wikiWikiEntry', params: { 
                id: entryId, 
                actionName: 'readProcessEntry', 
                ancestorsNames: this.tabsTitles 
            }})
                .catch(error => {
                    errorHandler(error)
                })
        },
        deleteProcessEntry(entry) {
            this.actionLoading = true
            this.$store.dispatch('wiki/deleteProcessEntry', entry.id)
                .then(() => {
                    this.processEntries.splice(this.processEntries.indexOf(entry), 1)
                    entry.processEntryAttachment.forEach(item => {
                        this.$store.dispatch('wiki/deleteAttachment', item.id)
                            .catch(error => {
                                errorHandler(error)
                            })
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                })
        },
        deleteProcessSubject(processSubject) {
            this.actionLoading = true
            this.$store.dispatch('toggleCircularLoader', true)
            this.$store.dispatch('wiki/deleteProcessSubject', processSubject.id)
                .then(() => {
                    this.currentLevelItems.splice( this.currentLevelItems.indexOf(processSubject), 1)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.$store.dispatch('toggleCircularLoader', false)
                })
        },
        closeDeleteProcessEntryDialog() {
            this.deleteProcessEntryDialog = false

            setTimeout(() => {
                this.processEntry = {}
            }, 500)
        },
        closeDeleteProcessSubjectDialog() {
            this.deleteProcessSubjectDialog = false

            setTimeout(() => {
                this.processSubject = {}
            }, 500)
        },
        showDeleteItemDialog(item) {
            if (item.processSubjectId) {
                this.processEntry = item
                this.deleteProcessEntryDialog = true
            } else {
                this.processSubject = item
                this.deleteProcessSubjectDialog = true
            }
        },
        changeFilter() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.searchLoading = false
                
                return
            }
            
            this.$store.dispatch('wiki/setWikiFilters', this.filters)
                .catch(error => {
                    errorHandler(error)
                })
            this.$v.$reset()
            this.resetTabs(this.tabsTitles[0])
            this.getProcessSubjects()
        },
        onClear() {
            this.clearFilter('text')
            this.$v.$reset()
        }
    }
}
</script>
